import { useState, useEffect } from 'react'
import { fetchWeatherForecast, getProductionSuggestions, buildWeatherCorrelations, autoLogTodayWeather, loadWeatherHistory, getDepartmentCoords } from '../../lib/weather'
import { getDailyCategorySummaryRange } from '../../lib/store'
import { useDepartment } from '../../context/DepartmentContext'
import { getCategories, KITCHEN_CATEGORIES_SEED } from '../../data/categories'
import { BAKERY_CATEGORIES_SEED } from '../../data/bakeryData'
import { YAWEHS_CATEGORIES_SEED } from '../../data/yawehsData'
import {
  Sun, Cloud, CloudRain, CloudDrizzle, CloudLightning, CloudFog, CloudSun,
  Droplets, Wind, Thermometer, TrendingUp, TrendingDown, Minus, AlertTriangle,
  ChevronDown, ChevronUp
} from 'lucide-react'

const WEATHER_ICONS = {
  sun: Sun,
  cloud: Cloud,
  'cloud-sun': CloudSun,
  'cloud-rain': CloudRain,
  'cloud-drizzle': CloudDrizzle,
  'cloud-lightning': CloudLightning,
  'cloud-fog': CloudFog,
}

function WeatherIcon({ icon, size = 20, className = '' }) {
  const Icon = WEATHER_ICONS[icon] || Cloud
  return <Icon size={size} className={className} />
}

export function CurrentWeather({ weather }) {
  if (!weather) return null
  const { current } = weather

  return (
    <div className="flex items-center gap-4">
      <WeatherIcon icon={current.icon} size={32} className="text-amber-400" />
      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold">{current.temp}°C</span>
          <span className="text-gray-400 text-sm">{current.label}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Droplets size={10} /> {current.humidity}%</span>
          <span className="flex items-center gap-1"><Wind size={10} /> {current.windSpeed} km/h</span>
        </div>
      </div>
    </div>
  )
}

export function ForecastStrip({ forecast }) {
  if (!forecast || !forecast.length) return null

  const today = new Date().toISOString().split('T')[0]

  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {forecast.map(day => {
        const isToday = day.date === today
        const dayLabel = isToday ? 'Today' : new Date(day.date + 'T00:00').toLocaleDateString('en-ZA', { weekday: 'short' })
        return (
          <div
            key={day.date}
            className={`flex-shrink-0 flex flex-col items-center gap-1 rounded-lg px-3 py-2 min-w-[70px] ${
              isToday ? 'bg-brand-900/50 border border-brand-700' : 'bg-gray-800/50'
            }`}
          >
            <span className={`text-xs font-medium ${isToday ? 'text-brand-400' : 'text-gray-400'}`}>{dayLabel}</span>
            <WeatherIcon icon={day.icon} size={18} className={day.precipitation > 5 ? 'text-blue-400' : 'text-amber-400'} />
            <div className="flex items-center gap-1 text-xs">
              <span className="text-white font-medium">{Math.round(day.tempMax)}°</span>
              <span className="text-gray-500">{Math.round(day.tempMin)}°</span>
            </div>
            {day.precipitation > 0 && (
              <span className="text-[10px] text-blue-400 flex items-center gap-0.5">
                <Droplets size={8} />{day.precipitation.toFixed(0)}mm
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function ProductionSuggestionsPanel({ suggestions, tomorrowForecast }) {
  const [expanded, setExpanded] = useState(true)

  if (!tomorrowForecast && (!suggestions || suggestions.length === 0)) return null

  const getSuggestionStyle = (type) => {
    switch (type) {
      case 'increase': return 'border-green-800 bg-green-950/30'
      case 'reduce': return 'border-red-800 bg-red-950/30'
      case 'maintain': return 'border-gray-700 bg-gray-800/30'
      default: return 'border-gray-700 bg-gray-800/30'
    }
  }

  const getSuggestionIcon = (type) => {
    switch (type) {
      case 'increase': return <TrendingUp size={14} className="text-green-400" />
      case 'reduce': return <TrendingDown size={14} className="text-red-400" />
      case 'maintain': return <Minus size={14} className="text-gray-400" />
      default: return <Minus size={14} className="text-gray-400" />
    }
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-800/50"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-400" />
          <span className="text-sm font-medium">Tomorrow's Production Suggestions</span>
          <span className="text-xs bg-brand-900 text-brand-400 px-1.5 py-0.5 rounded">{suggestions.length}</span>
        </div>
        {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          {tomorrowForecast && (
            <div className="flex items-center gap-3 bg-gray-800/50 rounded-lg p-3 mb-2">
              <WeatherIcon icon={tomorrowForecast.icon} size={24} className={tomorrowForecast.precipitation > 5 ? 'text-blue-400' : 'text-amber-400'} />
              <div>
                <p className="text-sm text-white font-medium">
                  Tomorrow: {tomorrowForecast.label}, {Math.round(tomorrowForecast.tempMax)}°C / {Math.round(tomorrowForecast.tempMin)}°C
                </p>
                <p className="text-xs text-gray-400">
                  {tomorrowForecast.precipitation > 0 ? `${tomorrowForecast.precipitation.toFixed(1)}mm rain expected` : 'No rain expected'}
                  {tomorrowForecast.windMax ? ` | Wind up to ${Math.round(tomorrowForecast.windMax)} km/h` : ''}
                </p>
              </div>
            </div>
          )}
          {suggestions.length === 0 ? (
            <p className="text-xs text-gray-500 py-2">No production suggestions available for this department yet.</p>
          ) : suggestions.map((s, i) => (
            <div key={i} className={`border rounded-lg p-3 ${getSuggestionStyle(s.type)}`}>
              <div className="flex items-start gap-2">
                {getSuggestionIcon(s.type)}
                <div className="flex-1">
                  <p className="text-sm text-gray-200">{s.message}</p>
                  {s.categories && s.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {s.categories.map(cat => (
                        <span key={cat} className="text-[10px] bg-gray-700/50 text-gray-300 px-1.5 py-0.5 rounded capitalize">{cat}</span>
                      ))}
                    </div>
                  )}
                  {s.datadriven && (
                    <span className="inline-block mt-1 text-[10px] bg-brand-900/50 text-brand-400 px-1.5 py-0.5 rounded">Data-driven</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Main weather dashboard section that ties everything together
export default function WeatherDashboard() {
  const { currentDepartmentId } = useDepartment()
  const [weather, setWeather] = useState(null)
  const [suggestions, setSuggestions] = useState([])
  const [tomorrowForecast, setTomorrowForecast] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function loadWeather() {
      setLoading(true)
      try {
        // Fetch forecast with department-specific coordinates
        const coords = getDepartmentCoords(currentDepartmentId)
        const data = await fetchWeatherForecast(coords.lat, coords.lon)
        if (cancelled || !data) { setLoading(false); return }
        setWeather(data)

        // Auto-log today's weather
        await autoLogTodayWeather()

        // Build correlations from historical data
        const today = new Date().toISOString().split('T')[0]
        const d = new Date()
        d.setDate(d.getDate() - 90)
        const startDate = d.toISOString().split('T')[0]

        const catSummaries = getDailyCategorySummaryRange(currentDepartmentId, startDate, today)
        const weatherHistory = loadWeatherHistory()
        const correlations = buildWeatherCorrelations(catSummaries, weatherHistory)

        // Tomorrow's forecast
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        const tomorrowStr = tomorrow.toISOString().split('T')[0]
        const tmrwData = data.forecast.find(f => f.date === tomorrowStr)

        if (tmrwData) {
          if (!cancelled) setTomorrowForecast(tmrwData)
          // Get categories for this department to filter suggestions
          const deptCategories = currentDepartmentId === 'yawehs' ? YAWEHS_CATEGORIES_SEED
            : currentDepartmentId === 'bakery' ? BAKERY_CATEGORIES_SEED
            : getCategories() // kitchen (includes custom categories)
          const sug = getProductionSuggestions(tmrwData, correlations, deptCategories)
          if (!cancelled) setSuggestions(sug)
        }
      } catch (err) {
        console.warn('Weather load failed:', err)
      }
      if (!cancelled) setLoading(false)
    }

    loadWeather()
    return () => { cancelled = true }
  }, [currentDepartmentId])

  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Cloud size={16} className="animate-pulse" />
          Loading weather data...
        </div>
      </div>
    )
  }

  if (!weather) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <p className="text-gray-500 text-sm">Weather data unavailable</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Weather Widget */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Weather</h3>
          <span className="text-[10px] text-gray-600">{getDepartmentCoords(currentDepartmentId).label}</span>
        </div>
        <CurrentWeather weather={weather} />
        <ForecastStrip forecast={weather.forecast} />
      </div>

      {/* Production Suggestions */}
      <ProductionSuggestionsPanel suggestions={suggestions} tomorrowForecast={tomorrowForecast} />
    </div>
  )
}
